import type { inferRouterOutputs } from '@trpc/server';
import type { AppRouter } from '@/types/appRouter';

type RouterOutputs = inferRouterOutputs<AppRouter>;

export type EntryOptimizerJob = RouterOutputs['entryOptimizer']['listJobs']['jobs'][number];
export type EntryOptimizerCandidate = RouterOutputs['entryOptimizer']['listCandidates']['candidates'][number];
export type EntryOptimizerCandidateFold = RouterOutputs['entryOptimizer']['candidateFolds']['folds'][number];
export type EntryOptimizerParameterScan = RouterOutputs['entryOptimizer']['parameterScans']['scans'][number];
export type EntryOptimizerEnvelopeRun = RouterOutputs['entryOptimizer']['listEnvelopeRuns']['runs'][number];
export type EntryOptimizerPreflight = RouterOutputs['entryOptimizer']['preflight']['preflight'];
export type EntryOptimizerPromotion = RouterOutputs['entryOptimizer']['promoteCandidate']['promotion'];

export type EntryOptimizerJobStatus = EntryOptimizerJob['status'];

export interface EntryOptimizerScanPoint {
    value: number;
    entryCount: number;
    meanProfit: number;
    lift: number;
    sweetScore: number;
    winRate: number;
}

export const ENTRY_OPTIMIZER_ACTIVE_JOB_STATUSES: EntryOptimizerJobStatus[] = ['queued', 'materializing', 'optimizing', 'validating'];

export const isEntryOptimizerJobActive = (job: EntryOptimizerJob): boolean => ENTRY_OPTIMIZER_ACTIVE_JOB_STATUSES.includes(job.status);

// ---------------------------------------------------------------------------
// Unified "tuning run" — collapses the internal two-stage pipeline (a
// data-collection run, then the analytic tuning over it) into one thing the
// user actually means: "tune this entry and tell me what's better."
// ---------------------------------------------------------------------------

export type TuningRunPhase = 'preparing' | 'ready' | 'tuning' | 'done' | 'failed';

export interface TuningRun {
    /** The data-collection run (internal "envelope"); always present. */
    run: EntryOptimizerEnvelopeRun;
    /** The tuning pass over that data, once started. */
    job: EntryOptimizerJob | null;
    phase: TuningRunPhase;
    statusLabel: string;
    statusTone: 'success' | 'danger' | 'info' | 'neutral' | 'warning';
    /** Settings that beat the current entry and passed validation (done runs only). */
    improvements: number | null;
    createdAt: string;
}

export const isTuningRunActive = (tuningRun: TuningRun): boolean => tuningRun.phase === 'preparing' || tuningRun.phase === 'tuning';

/**
 * Joins each data-collection run with its tuning pass into one logical run with a
 * plain-language story status. Study-internal jobs (those belonging to a variation
 * search) are excluded — they are presented under their study, not as standalone runs.
 */
export const buildTuningRuns = (runs: EntryOptimizerEnvelopeRun[], jobs: EntryOptimizerJob[]): TuningRun[] => {
    const jobByEnvelope = new Map<number, EntryOptimizerJob>();

    for (const job of jobs) {
        if (job.studyId != null) continue;

        const existing = jobByEnvelope.get(job.envelopeExperimentId);

        if (!existing || job.id > existing.id) jobByEnvelope.set(job.envelopeExperimentId, job);
    }

    return runs
        .map((run): TuningRun => {
            const job = jobByEnvelope.get(run.experimentId) ?? null;
            const base = { run, job, createdAt: run.createdAt };

            if (run.executionStatus === 'failed' || run.executionStatus === 'cancelled') {
                return { ...base, phase: 'failed', statusLabel: 'Data collection failed', statusTone: 'danger', improvements: null };
            }

            if (run.executionStatus !== 'completed') {
                return { ...base, phase: 'preparing', statusLabel: 'Preparing data…', statusTone: 'info', improvements: null };
            }

            if (!job) {
                return { ...base, phase: 'ready', statusLabel: 'Ready to tune', statusTone: 'warning', improvements: null };
            }

            if (job.status === 'failed') {
                return { ...base, phase: 'failed', statusLabel: 'Tuning failed', statusTone: 'danger', improvements: null };
            }

            if (job.status === 'cancelled') {
                return { ...base, phase: 'failed', statusLabel: 'Cancelled', statusTone: 'neutral', improvements: null };
            }

            if (job.status !== 'completed') {
                return { ...base, phase: 'tuning', statusLabel: 'Testing settings…', statusTone: 'info', improvements: null };
            }

            const improvements = job.candidatesAfterCV ?? 0;

            return {
                ...base,
                phase: 'done',
                statusLabel:
                    improvements > 0 ? `Done — ${improvements} improvement${improvements === 1 ? '' : 's'}` : 'Done — no improvement',
                statusTone: improvements > 0 ? 'success' : 'neutral',
                improvements
            };
        })
        .sort((left, right) => new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime());
};

export const isEntryOptimizerJobTerminal = (job: EntryOptimizerJob): boolean =>
    job.status === 'completed' || job.status === 'failed' || job.status === 'cancelled';

export const formatEntryOptimizerStatus = (status: EntryOptimizerJobStatus): string => status.charAt(0).toUpperCase() + status.slice(1);

export const getEntryOptimizerStatusTone = (status: EntryOptimizerJobStatus): 'danger' | 'info' | 'neutral' | 'success' => {
    if (status === 'completed') return 'success';
    if (status === 'failed') return 'danger';
    if (status === 'cancelled') return 'neutral';

    return 'info';
};

export const isEnvelopeRunActive = (run: EntryOptimizerEnvelopeRun): boolean =>
    run.executionStatus === 'queued' || run.executionStatus === 'running' || run.executionStatus === 'draft';

export const getEnvelopeRunTone = (run: EntryOptimizerEnvelopeRun): 'danger' | 'info' | 'neutral' | 'success' => {
    if (run.executionStatus === 'completed') return 'success';
    if (run.executionStatus === 'failed' || run.executionStatus === 'cancelled') return 'danger';

    return 'info';
};

export const parseScanPoints = (scan: EntryOptimizerParameterScan): EntryOptimizerScanPoint[] => {
    try {
        const points = JSON.parse(scan.scanJson) as EntryOptimizerScanPoint[];

        return [...points].sort((left, right) => left.value - right.value);
    } catch {
        return [];
    }
};

export const parseParamVector = (candidate: EntryOptimizerCandidate): Record<string, number | [number, number]> => {
    try {
        return JSON.parse(candidate.paramVectorJson) as Record<string, number | [number, number]>;
    } catch {
        return {};
    }
};

export const parseBlockingReasons = (candidate: EntryOptimizerCandidate): string[] => {
    if (!candidate.blockingReasonsJson) return [];

    try {
        return JSON.parse(candidate.blockingReasonsJson) as string[];
    } catch {
        return [];
    }
};

export const formatParamVector = (candidate: EntryOptimizerCandidate): string =>
    Object.values(parseParamVector(candidate))
        .map((value) => (Array.isArray(value) ? `[${value[0]}, ${value[1]}]` : String(value)))
        .join(', ');

export const formatMetric = (value: number | string | null, digits = 3): string => {
    if (value === null) return '—';

    const numeric = typeof value === 'string' ? Number(value) : value;

    if (Number.isNaN(numeric)) return '—';

    return numeric.toFixed(digits);
};

export const formatPercent = (value: number | string | null, digits = 1): string => {
    if (value === null) return '—';

    const numeric = typeof value === 'string' ? Number(value) : value;

    if (Number.isNaN(numeric)) return '—';

    return `${(numeric * 100).toFixed(digits)}%`;
};

export const formatTimestamp = (value: string | null): string => {
    if (!value) return '—';

    const date = new Date(value);

    return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString();
};

export const parsePositiveInt = (value: string | null): number | null => {
    if (!value) return null;

    const parsed = Number.parseInt(value, 10);

    return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
};

// ---------------------------------------------------------------------------
// Phase 7 — variant studies
// ---------------------------------------------------------------------------

export type EntryOptimizerStudy = RouterOutputs['entryOptimizer']['listStudies']['studies'][number];
export type EntryOptimizerStudyPreflight = RouterOutputs['entryOptimizer']['studyPreflight']['preflight'];
export type EntryOptimizerStudyVariant = EntryOptimizerStudyPreflight['grid'][number];

export interface EntryOptimizerStudyVariantResult {
    variantKey: string;
    values: Record<string, string>;
    summary: string;
    isIncumbent: boolean;
    stage: 'pilot' | 'full';
    jobId: number | null;
    bestCandidateId: number | null;
    bestResearchCandidateId?: number | null;
    researchCandidatePassesGates?: boolean | null;
    bestSelectionScore: number | null;
    adjustedScore: number | null;
    bestHoldoutLift: number | null;
    bestCvSignAgreement: number | null;
    entryCount: number | null;
    overlapWithIncumbent: number | null;
    error: string | null;
}

export interface EntryOptimizerStudyResults {
    verdict: 'variant_recommended' | 'keep_incumbent';
    recommendedVariantKey: string | null;
    runnableVariants: number;
    stageCompleted: 'pilot' | 'full';
    variants: EntryOptimizerStudyVariantResult[];
}

export const ENTRY_OPTIMIZER_ACTIVE_STUDY_STATUSES: EntryOptimizerStudy['status'][] = ['pilot_running', 'selecting', 'full_running'];

export const isEntryOptimizerStudyActive = (study: EntryOptimizerStudy): boolean =>
    ENTRY_OPTIMIZER_ACTIVE_STUDY_STATUSES.includes(study.status);

export const parseStudyResults = (study: EntryOptimizerStudy): EntryOptimizerStudyResults | null => {
    if (!study.resultSummaryJson) return null;

    try {
        return JSON.parse(study.resultSummaryJson) as EntryOptimizerStudyResults;
    } catch {
        return null;
    }
};

const STUDY_STATUS_LABELS: Record<EntryOptimizerStudy['status'], string> = {
    pilot_running: 'Pilot running',
    selecting: 'Selecting finalists',
    full_running: 'Full universe running',
    completed: 'Completed',
    failed: 'Failed',
    cancelled: 'Cancelled'
};

export const formatStudyStatus = (status: EntryOptimizerStudy['status']): string => STUDY_STATUS_LABELS[status] ?? status;

export const getStudyStatusTone = (status: EntryOptimizerStudy['status']): 'success' | 'warning' | 'danger' | 'info' => {
    if (status === 'completed') return 'success';
    if (status === 'failed') return 'danger';
    if (status === 'cancelled') return 'warning';

    return 'info';
};

// ---------------------------------------------------------------------------
// Study leaderboard — reconcile every grid variant with its result so the page
// tells the whole funnel story: which variants reached full validation, which
// ran but produced no qualifying setting, and which were screened out in the
// cheap pilot round. The result summary only carries the full-stage variants;
// the rest is recovered from the study's own grid (gridJson) + axis ordering.
// ---------------------------------------------------------------------------

interface StudyGridVariant {
    variantKey: string;
    values: Record<string, string>;
    isIncumbent: boolean;
    runnable: boolean;
    reasons: string[];
}

interface StudyVariantOrdering {
    fastAxisId: string;
    slowAxisId: string;
}

export type StudyVariantOutcome =
    | 'incumbent'
    | 'recommended'
    | 'validated'
    | 'no_qualifying_setting'
    | 'screened_out_pilot'
    | 'not_runnable';

export interface StudyLeaderboardRow extends EntryOptimizerStudyVariantResult {
    outcome: StudyVariantOutcome;
    runnable: boolean;
    notRunnableReasons: string[];
}

export interface StudyLeaderboard {
    /** Results from the completed stage, including pilot-only conclusions, best score first. */
    finalists: StudyLeaderboardRow[];
    /** Ran in the pilot round but did not rank high enough to advance. */
    screenedOut: StudyLeaderboardRow[];
    /** Never ran — not a computable level pair. */
    notRunnable: StudyLeaderboardRow[];
    totalVariants: number;
    fullCount: number;
    screenedOutCount: number;
}

const parseStudyGrid = (study: Pick<EntryOptimizerStudy, 'gridJson'>): StudyGridVariant[] => {
    try {
        const grid = JSON.parse(study.gridJson) as StudyGridVariant[];

        return Array.isArray(grid) ? grid : [];
    } catch {
        return [];
    }
};

const parseStudyOrderings = (study: Pick<EntryOptimizerStudy, 'variantAxesJson'>): StudyVariantOrdering[] => {
    try {
        const axes = JSON.parse(study.variantAxesJson) as { orderings?: StudyVariantOrdering[] };

        return axes.orderings ?? [];
    } catch {
        return [];
    }
};

/** "sma-20/sma-50" — the fast/slow level pair, matching the result summary's labels. */
const deriveVariantSummary = (values: Record<string, string>, orderings: StudyVariantOrdering[]): string => {
    const ordering = orderings[0];

    if (ordering) {
        const fast = values[ordering.fastAxisId];
        const slow = values[ordering.slowAxisId];

        if (fast && slow) return `${fast}/${slow}`;
    }

    return Object.values(values).join('/');
};

export const buildStudyLeaderboard = (
    study: Pick<EntryOptimizerStudy, 'gridJson' | 'variantAxesJson'>,
    results: EntryOptimizerStudyResults
): StudyLeaderboard => {
    const grid = parseStudyGrid(study);
    const orderings = parseStudyOrderings(study);
    const resultByKey = new Map(results.variants.map((variant) => [variant.variantKey, variant]));

    const rows: StudyLeaderboardRow[] = grid.map((variant) => {
        const full = resultByKey.get(variant.variantKey);

        if (full) {
            const outcome: StudyVariantOutcome = full.isIncumbent
                ? 'incumbent'
                : results.recommendedVariantKey === full.variantKey
                  ? 'recommended'
                  : full.bestCandidateId !== null
                    ? 'validated'
                    : 'no_qualifying_setting';

            return { ...full, outcome, runnable: true, notRunnableReasons: [] };
        }

        return {
            variantKey: variant.variantKey,
            values: variant.values,
            summary: deriveVariantSummary(variant.values, orderings),
            isIncumbent: variant.isIncumbent,
            stage: 'pilot',
            jobId: null,
            bestCandidateId: null,
            bestSelectionScore: null,
            adjustedScore: null,
            bestHoldoutLift: null,
            bestCvSignAgreement: null,
            entryCount: null,
            overlapWithIncumbent: null,
            error: null,
            outcome: variant.runnable ? 'screened_out_pilot' : 'not_runnable',
            runnable: variant.runnable,
            notRunnableReasons: variant.reasons ?? []
        };
    });

    const isFinalist = (row: StudyLeaderboardRow): boolean =>
        row.outcome === 'incumbent' ||
        row.outcome === 'recommended' ||
        row.outcome === 'validated' ||
        row.outcome === 'no_qualifying_setting';

    const finalists = rows
        .filter(isFinalist)
        .sort((left, right) => (right.adjustedScore ?? Number.NEGATIVE_INFINITY) - (left.adjustedScore ?? Number.NEGATIVE_INFINITY));

    const screenedOut = rows.filter((row) => row.outcome === 'screened_out_pilot');
    const notRunnable = rows.filter((row) => row.outcome === 'not_runnable');

    return {
        finalists,
        screenedOut,
        notRunnable,
        totalVariants: grid.length,
        fullCount: finalists.filter((row) => row.stage === 'full').length,
        screenedOutCount: screenedOut.length
    };
};

export const getStudyOutcomeLabel = (outcome: StudyVariantOutcome): string => {
    switch (outcome) {
        case 'incumbent':
            return 'Your current entry';
        case 'recommended':
            return '★ Recommended';
        case 'validated':
            return 'Validated';
        case 'no_qualifying_setting':
            return 'No qualifying setting';
        case 'screened_out_pilot':
            return 'Screened out in pilot';
        case 'not_runnable':
            return 'Not testable';
    }
};

export const getStudyOutcomeTone = (outcome: StudyVariantOutcome): 'success' | 'danger' | 'info' | 'neutral' | 'warning' => {
    switch (outcome) {
        case 'recommended':
            return 'success';
        case 'validated':
            return 'info';
        case 'no_qualifying_setting':
            return 'warning';
        case 'not_runnable':
            return 'danger';
        case 'incumbent':
        case 'screened_out_pilot':
            return 'neutral';
    }
};

export const getStudyObjectiveLabel = (objective: EntryOptimizerStudy['objective']): string => {
    switch (objective) {
        case 'avg_profit':
            return 'average profit per trade';
        case 'total_profit':
            return 'total profit';
        case 'sweet_score':
            return 'the balanced score';
    }
};

/** Collapse a raw gate-blocking reason into a plain-language category. */
const categorizeBlockingReason = (reason: string): string | null => {
    const lower = reason.toLowerCase();

    if (lower.includes('sign agreement')) return 'inconsistent across time';
    if (lower.includes('holdout')) return 'no edge on unseen data';
    if (lower.includes('support') || lower.includes('unique entit')) return 'too few trades';
    if (lower.includes('no-gate baseline') || lower.includes('does not beat')) return 'no improvement over no gate';
    if (lower.includes('drawdown')) return 'drawdown too deep';

    return null;
};

/**
 * Plain-language explanation for a "no qualifying setting" variant: how many tuned
 * settings the sweep tested and the two failure modes that knocked out the most of
 * them. Drives the hover tooltip so users don't have to open the job to learn why.
 */
export const summarizeNoQualifyingReason = (candidates: EntryOptimizerCandidate[] | undefined): string => {
    const tested = (candidates ?? []).filter((candidate) => candidate.isIncumbent === 0);

    if (tested.length === 0) return 'No tuned setting passed validation.';

    const counts = new Map<string, number>();

    for (const candidate of tested) {
        const seen = new Set<string>();

        for (const reason of parseBlockingReasons(candidate)) {
            const category = categorizeBlockingReason(reason);

            if (category && !seen.has(category)) {
                seen.add(category);
                counts.set(category, (counts.get(category) ?? 0) + 1);
            }
        }
    }

    const top = [...counts.entries()]
        .sort((left, right) => right[1] - left[1])
        .slice(0, 2)
        .map(([category]) => category);

    const tail = top.length > 0 ? ` — most fell short on ${top.join(' and ')}` : '';

    return `${tested.length} setting${tested.length === 1 ? '' : 's'} tested; none passed validation${tail}.`;
};
